import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ScoreGauge, FadeInSection, SeverityBadge } from '@/components/shared/UiElements';
import {
  ArrowLeft, ArrowUpRight, ArrowDownRight, Minus, ChevronDown, Check,
  TrendingUp, AlertTriangle, ArrowRight, Download, Link2,
} from 'lucide-react';
import axios from 'axios';
import '@/styles/forms.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Severity and effort chips.

   SeverityBadge is the SITE's badge, imported rather than reimplemented - it
   carries the three-step meter alongside the word, so severity is never
   colour alone. A local copy was written here first and the style guard
   caught it: "there is exactly one severity chip implementation". It was
   right. The only thing this page adds is a smaller size, which is a CSS
   modifier, not a second component.

   Effort is a plain neutral chip; it has no ranking to encode. */
/* Dimension keys in the reader's words. Stored findings use both the long
   and the short spelling. Six, never seven. */
const DIMENSION_LABELS = {
  navigation: 'Navigation',
  visualhierarchy: 'Visual Hierarchy', visual: 'Visual Hierarchy',
  frictionpoints: 'Friction Points', friction: 'Friction Points',
  accessibility: 'Accessibility',
  conversiondesign: 'Conversion Design', conversion: 'Conversion Design',
  mobileux: 'Mobile UX', mobile: 'Mobile UX',
};
const dimensionLabel = (key) =>
  DIMENSION_LABELS[String(key || '').toLowerCase().replace(/[^a-z]/g, '')] || key;

const TREND_LABELS = { worse: 'Worse', better: 'Better', unchanged: 'Unchanged' };

const SeverityChip = ({ severity }) => {
  const key = String(severity || '').toLowerCase();
  if (!['critical', 'major', 'minor'].includes(key)) return null;
  return <SeverityBadge severity={key} />;
};

const EffortChip = ({ effort }) => {
  if (!effort) return null;
  return <span className="uxa-chip uxa-chip--sm">{String(effort)} effort</span>;
};

const DeltaIndicator = ({ delta, size = 'md' }) => {
  // null/undefined is "not comparable", which is not the same statement as
  // "no change". A dimension only one audit assessed has no delta at all.
  if (delta === null || delta === undefined) {
    return <span className="uxa-ink-2 text-xs font-medium">Excluded</span>;
  }
  if (delta === 0) {
    return (
      <span className={`uxa-ink-2 ${size === 'lg' ? 'text-base' : 'text-xs'} font-semibold flex items-center gap-1`}>
        <Minus className="w-3 h-3" /> 0
      </span>
    );
  }
  const positive = delta > 0;
  return (
    <span
      className={`font-semibold flex items-center gap-0.5 ${positive ? 'uxa-delta-up' : 'uxa-delta-down'} ${size === 'lg' ? 'text-base' : 'text-sm'}`}
      data-testid={`delta-${positive ? 'up' : 'down'}`}
    >
      {positive ? <ArrowUpRight className={size === 'lg' ? 'w-4 h-4' : 'w-3.5 h-3.5'} /> : <ArrowDownRight className={size === 'lg' ? 'w-4 h-4' : 'w-3.5 h-3.5'} />}
      {positive ? '+' : ''}{delta}
    </span>
  );
};

/* One dimension, as ONE row: label, both sides side by side, delta.
   The previous layout stacked label+delta above two bars, which pushed six
   dimensions down a full screen and put the number you compare furthest from
   the number you compare it with.

   Bar colour encodes WHICH AUDIT, not the score - earlier is the accent's
   tint, later is the accent. That is a series colour, not a score band:
   the score itself is printed above every bar, so nothing here is carried
   by colour alone. */
const DimensionRow = ({ label, scoreA, scoreB, delta, assessedA, assessedB }) => {
  const Side = ({ caption, score, assessed, tone, delay }) => (
    <div className="min-w-0">
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <span className="uxa-microlabel">{caption}</span>
        <span className={assessed ? 'text-sm font-bold text-[var(--ink-1)]' : 'text-sm uxa-ink-2'}>
          {assessed ? score : 'Not assessed'}
        </span>
      </div>
      <div className="uxa-cmp-track">
        {assessed
          ? <motion.div className={`uxa-cmp-fill uxa-cmp-fill--${tone}`}
              initial={{ width: 0 }} animate={{ width: `${score}%` }}
              transition={{ duration: 0.8, delay }} />
          /* Hatched, not a zero-width bar: an empty track and a zero score
             must not look the same. That equivalence was the reported bug. */
          : <div className="uxa-track-absent" />}
      </div>
    </div>
  );
  return (
    <div className="uxa-cmp-row" data-testid={`dim-compare-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <span className="uxa-cmp-row-label">{label}</span>
      <Side caption="Earlier" score={scoreA} assessed={assessedA} tone="earlier" delay={0.15} />
      <Side caption="Later" score={scoreB} assessed={assessedB} tone="later" delay={0.3} />
      <span className="uxa-cmp-row-delta"><DeltaIndicator delta={delta} /></span>
    </div>
  );
};

// `matchTarget` is the other side's input_data. Audits of a different page are
// still listed but pushed below a labelled divider: a customer who genuinely
// wants a cross-site look can still have one, they just cannot do it by
// accident.
function AuditSelector({ audits, selectedId, onChange, label, matchTarget }) {
  const [open, setOpen] = useState(false);
  const selected = audits.find(a => a.audit_id === selectedId);
  const sameTarget = (a) => !matchTarget || (a.input_data || '') === matchTarget;
  const ordered = {
    same: audits.filter(sameTarget),
    other: audits.filter(a => !sameTarget(a)),
  };
  const Row = ({ a, muted }) => (
    <button key={a.audit_id} onClick={() => { onChange(a.audit_id); setOpen(false); }}
      className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors uxa-border-1 border-b last:border-0 hover:bg-[var(--surface-sand)] ${a.audit_id === selectedId ? 'bg-[var(--surface-sand)]' : ''}`}>
      <span className="flex-1 min-w-0">
        <span className={`block text-sm font-semibold truncate ${muted ? 'uxa-ink-2' : 'text-[var(--ink-1)]'}`}>{a.app_name || 'Untitled'}</span>
        <span className="block text-xs uxa-ink-2 truncate">
          {new Date(a.created_at).toLocaleDateString()} &middot; {a.input_data || a.industry || 'General'}
        </span>
      </span>
      <span className={`text-sm font-bold flex-shrink-0 ${muted ? 'uxa-ink-2' : 'text-[var(--ink-1)]'}`}>{a.overall_score}</span>
      {a.audit_id === selectedId && <Check className="w-4 h-4 uxa-delta-up flex-shrink-0" />}
    </button>
  );
  return (
    <div className="relative" data-testid={`selector-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <label className="uxa-microlabel block mb-2">{label}</label>
      <button onClick={() => setOpen(!open)} className="uxa-cmp-select">
        <span className="min-w-0 flex-1">
          {selected ? (
            <>
              <span className="block text-base font-semibold text-[var(--ink-1)] truncate">{selected.app_name || 'Untitled'}</span>
              <span className="block text-sm uxa-ink-2">{new Date(selected.created_at).toLocaleDateString()} &middot; Score: {selected.overall_score}</span>
            </>
          ) : <span className="text-base uxa-ink-2">Select an audit…</span>}
        </span>
        <ChevronDown className={`w-4 h-4 uxa-ink-2 flex-shrink-0 ml-2 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
          className="absolute z-20 top-full mt-1 w-full bg-[var(--surface-white)] uxa-border-2 border rounded-xl shadow-lg max-h-64 overflow-y-auto">
          {ordered.same.map(a => <Row key={a.audit_id} a={a} />)}
          {ordered.other.length > 0 && (
            <>
              <p className="px-4 py-2 uxa-microlabel bg-[var(--surface-sand)] uxa-border-1 border-y">
                Different page &mdash; deltas will not be meaningful
              </p>
              {ordered.other.map(a => <Row key={a.audit_id} a={a} muted />)}
            </>
          )}
        </motion.div>
      )}
    </div>
  );
}

export default function CompareAuditsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [audits, setAudits] = useState([]);
  const [auditA, setAuditA] = useState(searchParams.get('a') || '');
  const [auditB, setAuditB] = useState(searchParams.get('b') || '');
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingAudits, setLoadingAudits] = useState(true);
  // True once the page has reordered a pair the customer entered backwards.
  // A silent reorder is its own small deception: the selection jumps and
  // nothing says why.
  const [swapped, setSwapped] = useState(false);

  // Fetch all audits for selectors
  useEffect(() => {
    axios.get(`${API}/audits`, { withCredentials: true })
      .then(r => { setAudits(r.data); setLoadingAudits(false); })
      .catch(() => setLoadingAudits(false));
  }, []);

  /* ── EARLIER IS ALWAYS THE OLDER ONE ──────────────────────────────────────
     The two selectors are labelled "Earlier audit" and "Later audit" and
     nothing made that true. Pick them the other way round and every delta on
     the page inverts: a genuine improvement is drawn and counted as a
     regression, with a correct-looking arrow on it. Nothing on the page shows
     the reader which way the pair was ordered, so there is no tell.

     The pair is ordered by created_at before it is used, wherever it enters —
     a click on either selector, or `?a=&b=` on a shared link. The selectors
     are updated too, so what the page shows is the pair it actually compared.

     NO SWAP WHEN THE DATES CANNOT BE READ. `_created_at_sort_key` on the
     backend documents why: created_at is usually an ISO string but historical
     rows hold other shapes, and a row can be missing it. An unreadable date is
     not a licence to guess an order — the selection is left exactly as the
     customer made it, and the labels stay honest by saying nothing extra. */
  const createdMs = (id) => {
    const a = audits.find(x => x.audit_id === id);
    if (!a || !a.created_at) return null;
    const t = new Date(a.created_at).getTime();
    return Number.isFinite(t) ? t : null;
  };
  const orderPair = (idA, idB) => {
    const ta = createdMs(idA);
    const tb = createdMs(idB);
    if (ta === null || tb === null || ta <= tb) return [idA, idB];
    return [idB, idA];
  };

  // Auto-select if only params but no comparison yet
  useEffect(() => {
    // Ordering needs the audit list's dates, so nothing happens until it lands.
    // A fetch fired before then would go out in whatever order the URL carried.
    if (!audits.length) return;
    if (!(auditA && auditB && auditA !== auditB)) return;
    const [x, y] = orderPair(auditA, auditB);
    if (x !== auditA || y !== auditB) {
      // Re-enters this effect with the ordered pair, which then fetches once.
      setSwapped(true);
      setAuditA(x);
      setAuditB(y);
      return;
    }
    if (!comparison) fetchComparison(x, y);
    // Deliberate dep list: the two selected audits and the list that dates
    // them. fetchComparison and comparison are read, not tracked. Same note as
    // AuditReportPage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditA, auditB, audits]);

  const fetchComparison = async (idA, idB) => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/audits/compare/${idA}/${idB}`, { withCredentials: true });
      setComparison(resp.data);
      setSearchParams({ a: idA, b: idB });
    } catch (e) {
      console.error('Compare failed', e);
    } finally {
      setLoading(false);
    }
  };

  const handleCompare = () => {
    if (!(auditA && auditB && auditA !== auditB)) return;
    const [x, y] = orderPair(auditA, auditB);
    if (x !== auditA || y !== auditB) { setSwapped(true); setAuditA(x); setAuditB(y); }
    fetchComparison(x, y);
  };

  const deliveredAudits = useMemo(() => audits.filter(a => a.status === 'DELIVERED'), [audits]);

  // The page each selection audited, so the opposite selector can group by it.
  const targetOf = (id) => {
    const a = audits.find(x => x.audit_id === id);
    return a ? (a.input_data || '') : '';
  };

  // Audits scored by different methods (scoring v1 vs v2): no score change
  // is shown, as a gain or a loss. The backend already withholds the deltas.
  const scoringDiffers = Boolean(comparison && comparison.scoring_versions_differ);

  const improvementSummary = useMemo(() => {
    if (!comparison) return null;
    const dims = comparison.dimensions;
    // Count only dimensions both audits assessed. `d.delta > 0` on a null
    // delta is false in JS, but `d.delta < 0` on a null is ALSO false, so the
    // old counts happened to be right only because the backend had already
    // coerced the null to 0 — and that coercion is the bug. Gate on the flag.
    const improved = dims.filter(d => d.assessed && d.delta > 0).length;
    const regressed = dims.filter(d => d.assessed && d.delta < 0).length;
    const unchanged = dims.filter(d => d.assessed && d.delta === 0).length;
    const notAssessed = dims.filter(d => !d.assessed).length;
    return { improved, regressed, unchanged, notAssessed };
  }, [comparison]);

  // The headline used to be "+14 points, Improved" — true, and inert. Every
  // number below is already on the page; the sentence just says what they
  // mean together.
  const headline = useMemo(() => {
    if (!comparison) return null;
    const f = comparison.findings_summary;
    const parts = [];
    // Findings are paired on evidence and measured elements
    // (backend/audit/matching.py), so Resolved and New mean what the sublines
    // under the lists say. Possible matches are counted as neither.
    if (f.resolved_count > 0) parts.push(`${f.resolved_count} resolved`);
    if (f.new_count > 0) parts.push(`${f.new_count} new`);
    if (f.persistent_count > 0) parts.push(`${f.persistent_count} still open`);
    if (f.possibly_same_count > 0) parts.push(`${f.possibly_same_count} possibly the same`);
    const gain = comparison.biggest_gain;
    const drop = comparison.biggest_drop;

    let lead;
    if (scoringDiffers) lead = 'Scores are not compared.';
    else if (comparison.overall_delta > 0) lead = `Up ${comparison.overall_delta} points.`;
    else if (comparison.overall_delta < 0) lead = `Down ${Math.abs(comparison.overall_delta)} points.`;
    else lead = 'The overall score did not move.';

    const movement = scoringDiffers
      ? comparison.scoring_note
      : gain
      ? `Biggest gain: ${gain.label}, +${gain.delta}.`
      : drop
        ? `Biggest drop: ${drop.label}, ${drop.delta}.`
        : 'No dimension moved.';

    return {
      lead,
      body: parts.length ? `${parts.join(', ')}.` :'No findings changed between the two audits.',
      movement,
      gain,
      drop,
    };
  }, [comparison]);

  /* Copy the comparison's own link. The URL already carries ?a=&b=, so the
     recipient lands on this exact pair. */
  const [shareState, setShareState] = useState('idle');
  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShareState('copied');
      setTimeout(() => setShareState('idle'), 2000);
    } catch {
      setShareState('failed');
      setTimeout(() => setShareState('idle'), 2000);
    }
  };

  return (
    <div className="min-h-screen pt-20" style={{ background: 'var(--surface-sand)' }}>
      <div className="mx-auto px-6 py-10" style={{ maxWidth: 1200 }}>

        {/* Back link. A labelled link, not a bare glyph: an unlabelled arrow
            is a guess, and the label costs one line. */}
        <Link to="/dashboard" className="uxa-backlink" data-testid="compare-back">
          <ArrowLeft className="w-4 h-4" aria-hidden="true" />
          Back to audits
        </Link>

        <header className="flex flex-wrap items-end justify-between gap-6 mt-6 mb-10">
          <div className="min-w-0">
            <h1 className="uxa-h1" data-testid="compare-title">Compare Audits</h1>
            <p className="uxa-lead uxa-ink-2 mt-2">Track UX improvements over time</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Browser print, deliberately: there is no server-rendered
                comparison PDF yet. Labelled for what it does. */}
            <button type="button" onClick={() => window.print()} className="uxa-pill" data-testid="compare-print">
              Download PDF <Download className="w-4 h-4" aria-hidden="true" />
            </button>
            <button type="button" onClick={handleShare} className="uxa-pill" data-testid="compare-share">
              {shareState === 'copied' ? 'Link copied' : shareState === 'failed' ? 'Copy failed' : 'Share'}
              {shareState === 'copied' ? <Check className="w-4 h-4" aria-hidden="true" /> : <Link2 className="w-4 h-4" aria-hidden="true" />}
            </button>
            <span aria-live="polite" className="sr-only">
              {shareState === 'copied' ? 'Link copied to clipboard' : shareState === 'failed' ? 'Could not copy the link' : ''}
            </span>
          </div>
        </header>

        {/* Selectors */}
        <FadeInSection>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 items-end mb-8">
            {/* The reorder note is cleared on every fresh selection and set again
                by the ordering effect only if the NEW pair needs a swap, so it
                describes the pair on screen rather than a pair two clicks ago. */}
            <AuditSelector audits={deliveredAudits} selectedId={auditA}
              onChange={(id) => { setSwapped(false); setAuditA(id); }}
              label="Earlier audit" matchTarget={targetOf(auditB)} />
            <div className="hidden md:flex items-center justify-center pb-3">
              <span className="uxa-cmp-arrow" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
            </div>
            <AuditSelector audits={deliveredAudits} selectedId={auditB}
              onChange={(id) => { setSwapped(false); setAuditB(id); }}
              label="Later audit" matchTarget={targetOf(auditA)} />
          </div>
          {swapped && (
            <p className="text-sm uxa-ink-2 mb-6" role="status" data-testid="compare-swapped">
              Reordered by date: the earlier audit is on the left. Comparing them the other way round would report every improvement as a regression.
            </p>
          )}
          {auditA && auditB && auditA === auditB && (
            <p className="text-sm uxa-delta-down mb-6">Please select two different audits to compare.</p>
          )}
          {auditA && auditB && auditA !== auditB && !comparison && (
            <div className="mb-8 text-center">
              <button onClick={handleCompare} data-testid="compare-btn" className="uxa-submit uxa-submit--auto">
                Compare
                <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
              </button>
            </div>
          )}
        </FadeInSection>

        {loading && (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-[var(--ink-1)] border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {!loadingAudits && deliveredAudits.length < 2 && (
          <FadeInSection>
            <div className="uxa-cmp-card p-14 text-center">
              <span className="uxa-icon-tile uxa-icon-tile--neutral mx-auto mb-5"><TrendingUp className="w-6 h-6" /></span>
              <h2 className="uxa-h3 mb-3">Need at least 2 completed audits</h2>
              <p className="uxa-ink-2 text-sm mb-6 max-w-md mx-auto">Run another audit after making UX improvements, then come back here to see your progress.</p>
              <Link to="/audit/new" className="uxa-submit uxa-submit--auto">
                Run New Audit
                <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
              </Link>
            </div>
          </FadeInSection>
        )}

        {comparison && !loading && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>

            {/* Different pages: every delta below is arithmetic between
                unrelated pages, so say it before the reader reads them. */}
            {comparison.same_target === false && (
              <div className="uxa-cmp-card p-6 mb-6" data-testid="compare-different-targets">
                <div className="flex items-start gap-4">
                  <span className="uxa-icon-tile uxa-icon-tile--warn flex-shrink-0" aria-hidden="true">
                    <AlertTriangle className="w-5 h-5" />
                  </span>
                  <div className="min-w-0">
                    <p className="font-semibold text-[var(--ink-1)]">These audits are of different pages.</p>
                    <p className="uxa-ink-2 mt-1 break-words">
                      <span className="font-medium">{comparison.target_a || 'unknown'}</span>{' '}
                      compared against{' '}
                      <span className="font-medium">{comparison.target_b || 'unknown'}</span>.
                      {' '}Every score difference below is a comparison between two unrelated
                      pages, not a measure of progress on either one.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ── Score movement ───────────────────────────────────────── */}
            <div className="uxa-cmp-card p-8 mb-6" data-testid="overall-comparison">
              <div className="flex flex-wrap items-baseline justify-between gap-3 mb-6">
                <span className="uxa-microlabel">Score movement</span>
                {comparison.swapped && (
                  <span className="text-xs uxa-ink-2" data-testid="compare-swapped-notice">
                    Reordered by date so the earlier audit is the baseline.
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-8 items-center">
                <div className="text-center">
                  <p className="uxa-microlabel mb-3">Earlier</p>
                  <ScoreGauge score={comparison.audit_a.overall_score || 0} size={130} strokeWidth={7} />
                  <p className="font-semibold text-[var(--ink-1)] mt-3 truncate">{comparison.audit_a.app_name || 'Audit A'}</p>
                  <p className="text-sm uxa-ink-2">{new Date(comparison.audit_a.created_at).toLocaleDateString()}</p>
                </div>

                <div className="text-center">
                  <span className={`uxa-delta-tile ${scoringDiffers ? 'is-flat' : comparison.overall_delta > 0 ? 'is-up' : comparison.overall_delta < 0 ? 'is-down' : 'is-flat'}`}>
                    {scoringDiffers ? <span className="text-sm">Not compared</span> : <DeltaIndicator delta={comparison.overall_delta} size="lg" />}
                  </span>
                  <p className={`uxa-microlabel mt-2 ${!scoringDiffers && comparison.overall_delta > 0 ? 'uxa-delta-up' : !scoringDiffers && comparison.overall_delta < 0 ? 'uxa-delta-down' : ''}`}>
                    {scoringDiffers ? 'Scored differently' : comparison.overall_delta > 0 ? 'Points improved' : comparison.overall_delta < 0 ? 'Points lost' : 'No change'}
                  </p>
                  {scoringDiffers && (
                    <p className="text-sm uxa-ink-2 mt-2 max-w-xs mx-auto" role="note" data-testid="compare-scoring-note">
                      {comparison.scoring_note}
                    </p>
                  )}
                </div>

                <div className="text-center">
                  <p className="uxa-microlabel mb-3">Later</p>
                  <ScoreGauge score={comparison.audit_b.overall_score || 0} size={130} strokeWidth={7} />
                  <p className="font-semibold text-[var(--ink-1)] mt-3 truncate">{comparison.audit_b.app_name || 'Audit B'}</p>
                  <p className="text-sm uxa-ink-2">{new Date(comparison.audit_b.created_at).toLocaleDateString()}</p>
                </div>
              </div>

              <hr className="uxa-cmp-divider" />

              <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-8 items-center">
                {headline && (
                  <div data-testid="compare-headline">
                    {/* Findings lead; the score sentence comes second. v1 scores
                        move between runs of an unchanged page, so after a re-audit
                        the score must never be the headline (owner, 2026-10-06). */}
                    <h2 className="uxa-h2">{headline.body}</h2>
                    <p className="uxa-ink-2 mt-2">
                      {headline.lead} {headline.movement}
                      {comparison.dimensions_unassessed.length > 0 && (
                        <> {comparison.dimensions_unassessed.join(' and ')}{' '}
                          {comparison.dimensions_unassessed.length === 1 ? 'was' : 'were'} not
                          assessed on both audits, so {comparison.dimensions_unassessed.length === 1 ? 'it is' : 'they are'} excluded.
                        </>
                      )}
                    </p>
                  </div>
                )}
                {improvementSummary && (
                  <div className="uxa-statstrip">
                    {!scoringDiffers && (
                      <>
                        <div data-testid="summary-improved">
                          <span className="uxa-statstrip-n">{improvementSummary.improved}</span>
                          <span className="uxa-statstrip-l">Dimensions improved</span>
                        </div>
                        <div data-testid="summary-regressed">
                          <span className="uxa-statstrip-n">{improvementSummary.regressed}</span>
                          <span className="uxa-statstrip-l">Dimensions regressed</span>
                        </div>
                      </>
                    )}
                    <div data-testid="summary-issues">
                      <span className={`uxa-statstrip-n ${comparison.findings_summary.new_count > 0 ? 'is-alert' : ''}`}>
                        {comparison.findings_summary.new_count}
                      </span>
                      <span className="uxa-statstrip-l">New</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ── Dimension comparison ─────────────────────────────────── */}
            <FadeInSection delay={0.15}>
              <div className="uxa-cmp-card p-8 mb-6" data-testid="dimension-comparison">
                <div className="flex flex-wrap items-baseline justify-between gap-3 mb-2">
                  <h2 className="uxa-h3">Dimension Comparison</h2>
                  <span className="text-sm uxa-ink-2">Ordered by how much each dimension moved, largest first</span>
                </div>
                {comparison.dimensions.map(dim => (
                  <DimensionRow key={dim.key} label={dim.label} scoreA={dim.score_a} scoreB={dim.score_b}
                    delta={dim.delta} assessedA={dim.assessed_a} assessedB={dim.assessed_b} />
                ))}
                {comparison.dimensions_unassessed.length > 0 && (
                  <p className="text-sm uxa-ink-2 mt-4">
                    {comparison.dimensions_unassessed.join(' and ')} {comparison.dimensions_unassessed.length === 1 ? 'was' : 'were'} scored
                    by only one of the two audits, so there is nothing to compare.
                  </p>
                )}
              </div>
            </FadeInSection>

            {/* ── Findings ─────────────────────────────────────────────── */}
            <FadeInSection delay={0.25}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="uxa-cmp-card p-8" data-testid="resolved-findings">
                  <div className="flex items-center gap-3 mb-5">
                    <span className="uxa-icon-tile uxa-icon-tile--good" aria-hidden="true"><Check className="w-4 h-4" /></span>
                    <div>
                      <h2 className="uxa-h3">Resolved ({comparison.findings_summary.resolved_count})</h2>
                      <p className="text-sm uxa-ink-2">Not reported in the later run</p>
                    </div>
                  </div>
                  {comparison.findings_summary.resolved.length > 0 ? (
                    <ul className="uxa-findlist">
                      {/* No strikethrough and no muted grey. This is the work
                          the customer actually did; it was rendered as though
                          it had been deleted. The check mark carries "done". */}
                      {comparison.findings_summary.resolved.map((f, i) => (
                        <li key={i} className="uxa-finditem">
                          <span className="uxa-findmark uxa-findmark--good" aria-hidden="true"><Check className="w-3 h-3" /></span>
                          <div className="min-w-0">
                            <p className="text-[var(--ink-1)]">{f.title}</p>
                            <div className="flex flex-wrap gap-2 mt-2"><SeverityChip severity={f.severity} /></div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="uxa-ink-2">Every issue in the earlier run is still reported.</p>
                  )}
                </div>

                <div className="uxa-cmp-card p-8" data-testid="new-findings">
                  <div className="flex items-center gap-3 mb-5">
                    <span className="uxa-icon-tile uxa-icon-tile--warn" aria-hidden="true"><AlertTriangle className="w-4 h-4" /></span>
                    <div>
                      <h2 className="uxa-h3">New ({comparison.findings_summary.new_count})</h2>
                      <p className="text-sm uxa-ink-2">Not reported in the earlier run</p>
                    </div>
                  </div>
                  {comparison.findings_summary.new_issues.length > 0 ? (
                    <ul className="uxa-findlist">
                      {/* The single-audit report gives a recommended fix per
                          finding. Dropping it here told the customer they had
                          a problem and not what to do about it. */}
                      {comparison.findings_summary.new_issues.map((f, i) => (
                        <li key={i} className="uxa-finditem">
                          <span className="uxa-findmark uxa-findmark--new" aria-hidden="true" />
                          <div className="min-w-0">
                            <p className="text-[var(--ink-1)]">{f.title}</p>
                            <div className="flex flex-wrap gap-2 mt-2">
                              <SeverityChip severity={f.severity} />
                              <EffortChip effort={f.effort} />
                            </div>
                            {f.recommended_fix && (
                              <p className="uxa-ink-2 mt-2">
                                <span className="font-semibold text-[var(--ink-1)]">Fix: </span>{f.recommended_fix}
                              </p>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="uxa-ink-2">Every issue in the later run was already reported.</p>
                  )}
                </div>
              </div>
            </FadeInSection>

            {comparison.findings_summary.persistent_count > 0 && (
              <FadeInSection delay={0.35}>
                <div className="uxa-cmp-card p-8 mb-6" data-testid="persistent-findings">
                  <h2 className="uxa-h3 mb-5">Persistent Issues ({comparison.findings_summary.persistent_count})</h2>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                    {comparison.findings_summary.persistent.map((f, i) => (
                      <li key={i} className="uxa-finditem">
                        <span className="uxa-findmark uxa-findmark--flat" aria-hidden="true" />
                        <div className="min-w-0">
                          <p className="text-[var(--ink-1)]">{f.title}</p>
                          <div className="flex flex-wrap gap-2 mt-2"><SeverityChip severity={f.severity} /></div>
                          {f.dimension_earlier && dimensionLabel(f.dimension_earlier) !== dimensionLabel(f.dimension) && (
                            <p className="text-sm uxa-ink-2 mt-2">
                              Under {dimensionLabel(f.dimension)} now, {dimensionLabel(f.dimension_earlier)} in the earlier run
                            </p>
                          )}
                          {/* Counted from measured elements, never from the
                              severity label: a finding can be relabelled
                              without a single element changing. */}
                          {f.trend && (
                            <p className="text-sm uxa-ink-2 mt-2" data-testid="persistent-trend">
                              <span className="font-semibold text-[var(--ink-1)]">{TREND_LABELS[f.trend.label]}: </span>
                              {f.trend.newly_failing} newly failing, {f.trend.no_longer_reported} no longer reported
                            </p>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </FadeInSection>
            )}

            {comparison.findings_summary.possibly_same_count > 0 && (
              <FadeInSection delay={0.4}>
                <div className="uxa-cmp-card p-8 mb-6" data-testid="possibly-same-findings">
                  <h2 className="uxa-h3">Possibly the same ({comparison.findings_summary.possibly_same_count})</h2>
                  <p className="text-sm uxa-ink-2 mb-5">
                    Worded alike but not close enough to call one issue. Counted as neither resolved nor new.
                  </p>
                  <ul className="uxa-findlist">
                    {comparison.findings_summary.possibly_same.map((p, i) => (
                      <li key={i} className="uxa-finditem">
                        <span className="uxa-findmark uxa-findmark--flat" aria-hidden="true" />
                        <div className="min-w-0">
                          <p className="text-[var(--ink-1)]"><span className="font-semibold">Earlier: </span>{p.earlier.title}</p>
                          <p className="text-[var(--ink-1)] mt-1"><span className="font-semibold">Later: </span>{p.later.title}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </FadeInSection>
            )}

            <FadeInSection delay={0.45}>
              <div className="uxa-cmp-cta">
                <h2 className="uxa-h2">Keep improving</h2>
                <p className="uxa-ink-2 mt-3 mb-7 max-w-md mx-auto">
                  Run another audit after your next round of fixes to track continued progress.
                </p>
                <Link to="/audit/new" data-testid="compare-new-audit" className="uxa-submit uxa-submit--auto">
                  Run New Audit
                  <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
                </Link>
              </div>
            </FadeInSection>
          </motion.div>
        )}
      </div>
    </div>
  );
}
